import { DIFFICULTIES } from '../data/copy.js';
import { STORAGE, storageGet, storageSet } from '../utils/storageKeys.js';

/**
 * Song catalog shown after PLAY. The tile click is the trusted user gesture
 * that starts the game (and thus native audio playback).
 * A segmented switch picks the difficulty (persisted across sessions), and
 * each tile has a leaderboard button opening the world top 20.
 */
export default class CatalogScreen {
  constructor({ onBack, onSelect, leaderboardOverlay }) {
    this.onBack = onBack;
    this.onSelect = onSelect;
    this.leaderboardOverlay = leaderboardOverlay;
    this.gridEl = document.getElementById('catalog-grid');
    this.switchEl = document.getElementById('difficulty-switch');
    this.difficulty = this._loadDifficulty();
    this._songs = [];
    this._bestMap = {};

    document.getElementById('btn-catalog-back')?.addEventListener('click', () => this.onBack?.());

    this._renderSwitch();
  }

  _loadDifficulty() {
    const stored = storageGet(STORAGE.DIFFICULTY);
    return DIFFICULTIES.some((d) => d.id === stored) ? stored : 'normal';
  }

  _renderSwitch() {
    if (!this.switchEl) return;
    this.switchEl.textContent = '';
    for (const { id, label } of DIFFICULTIES) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'difficulty-switch__option';
      button.setAttribute('role', 'radio');
      button.dataset.diff = id;
      button.textContent = label;
      button.addEventListener('click', () => {
        this.difficulty = id;
        storageSet(STORAGE.DIFFICULTY, id);
        this._syncSwitch();
        this._syncRanks(); // best grade/score depends on the selected difficulty
      });
      this.switchEl.appendChild(button);
    }
    this._syncSwitch();
  }

  _syncSwitch() {
    if (!this.switchEl) return;
    for (const button of this.switchEl.children) {
      const active = button.dataset.diff === this.difficulty;
      button.classList.toggle('is-active', active);
      button.setAttribute('aria-checked', String(active));
    }
  }

  /**
   * @param {Array} songs
   * @param {Object} bestMap "songId|difficulty" -> { score, grade } (server + local merged)
   */
  render(songs, bestMap = null) {
    if (!this.gridEl) return;
    this._songs = songs;
    if (bestMap) this._bestMap = bestMap;
    this.gridEl.textContent = '';

    for (const song of songs) {
      const wrap = document.createElement('div');
      wrap.className = 'catalog-tile';
      wrap.setAttribute('role', 'listitem');

      const tile = document.createElement('button');
      tile.type = 'button';
      tile.className = 'catalog-tile__main';

      const title = document.createElement('strong');
      title.className = 'catalog-tile__title';
      title.textContent = String(song.title ?? '').slice(0, 48).toUpperCase();

      const artist = document.createElement('span');
      artist.className = 'catalog-tile__artist';
      artist.textContent = String(song.artist ?? '').slice(0, 48).toUpperCase();

      const styles = document.createElement('span');
      styles.className = 'catalog-tile__styles';
      styles.textContent = (song.styles || []).slice(0, 3).join(' · ');

      const meta = document.createElement('span');
      meta.className = 'catalog-tile__meta';
      const minutes = Math.floor(song.duration / 60);
      const seconds = String(Math.round(song.duration % 60)).padStart(2, '0');
      meta.textContent = `${Math.round(song.bpm)} BPM · ${minutes}:${seconds}`;

      tile.append(title, artist, styles, meta);
      tile.addEventListener('click', () => this.onSelect?.(song, this.difficulty));

      const lbButton = document.createElement('button');
      lbButton.type = 'button';
      lbButton.className = 'catalog-tile__lb';
      lbButton.setAttribute('aria-label', `Classement de ${song.title}`);
      lbButton.textContent = 'TOP ✦';
      lbButton.addEventListener('click', (event) => {
        event.stopPropagation();
        this.leaderboardOverlay?.open(song, this.difficulty);
      });

      // Personal best panel, visually detached on the right of the tile.
      const rank = document.createElement('div');
      rank.className = 'catalog-tile__rank';
      rank.dataset.songId = String(song.id);
      rank.setAttribute('aria-label', `Meilleur rang sur ${song.title}`);

      const grade = document.createElement('span');
      grade.className = 'catalog-tile__rank-grade';

      const score = document.createElement('span');
      score.className = 'catalog-tile__rank-score';

      rank.append(grade, score);
      this._fillRank(rank, song);

      wrap.append(tile, lbButton, rank);
      this.gridEl.appendChild(wrap);
    }
  }

  /** Update best grade/score badges without rebuilding the grid. */
  setBestMap(bestMap) {
    this._bestMap = bestMap || {};
    this._syncRanks();
  }

  _syncRanks() {
    if (!this.gridEl) return;
    for (const rank of this.gridEl.querySelectorAll('.catalog-tile__rank')) {
      const song = this._songs.find((s) => String(s.id) === rank.dataset.songId);
      if (song) this._fillRank(rank, song);
    }
  }

  _fillRank(rankEl, song) {
    const best = this._bestMap[`${song.id}|${this.difficulty}`] ?? null;
    const gradeEl = rankEl.querySelector('.catalog-tile__rank-grade');
    const scoreEl = rankEl.querySelector('.catalog-tile__rank-score');
    rankEl.dataset.grade = best ? best.grade : 'none';
    if (best) {
      gradeEl.textContent = best.grade;
      scoreEl.textContent = best.score.toLocaleString('fr-FR');
    } else {
      gradeEl.textContent = '—';
      scoreEl.textContent = '';
    }
  }
}
